<template>
  <input
    :id="id"
    :name="name"
    :type="type"
    :required="required"
    :disabled="disabled"
    :pattern="pattern"
    :class="classes"
    :value="modelValue"
    :aria-required="required"
    :aria-invalid="required && !modelValue"
    @input.prevent="handleInput"
  />
</template>

<script lang="ts">
import { defineComponent, toRefs } from 'vue';
import { TextInputType, useInput } from './inputs';

export default defineComponent({
  name: 'Input',
  props: {
    id: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    type: {
      type: String as () => TextInputType,
      default: 'text',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    pattern: {
      type: String,
    },
    patternModifiers: {
      type: Object,
      default: () => ({}),
    },
    size: {
      type: Number,
    },
    modelValue: {
      type: String,
      default: '',
    },
    modelModifiers: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['keypress', 'update:modelValue'],
  setup(props, { emit }) {
    const { required, disabled, modelValue } = toRefs(props);

    const { classes } = useInput(required, disabled, modelValue);

    const handleInput = (event: Event) => {
      const target = event.target as HTMLInputElement;
      let value: string | undefined = target.value;

      if (props.pattern && props.patternModifiers.input) {
        const regex = new RegExp(props.pattern);
        const matches = regex.exec(value);

        // if any part matches, keep the first part, otherwise, clear
        matches
          ? (value = target.value = matches[0])
          : (value = target.value = '');
      }

      if (props.size) {
        if (value.length >= props.size) {
          value = value.substr(0, props.size);
          target.value = value;
        }
      }

      const modifierMap = new Map<string, (value: string) => string>([
        ['uppercase', (value) => value.toLocaleUpperCase()],
        ['lowercase', (value) => value.toLocaleLowerCase()],
        [
          'capitalize',
          (value) => value.charAt(0).toLocaleUpperCase() + value.slice(1),
        ],
      ]);

      if (value) {
        Object.keys(props.modelModifiers).forEach((modifier) => {
          const func = modifierMap.get(modifier);

          if (func && value) {
            value = func(value);
          }
        });
      }

      !(value == modelValue.value) && emit('update:modelValue', value);
    };

    return {
      classes,
      handleInput,
    };
  },
});
</script>
